import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './CANTelemetry.css';
import { toast } from 'react-toastify';
import {
  MdRefresh, MdExpandMore, MdCheckCircle, MdWarning, MdError,
  MdSearch, MdTune, MdClose, MdAutorenew, MdOutlineCircle,
  MdOutlineAvTimer, MdCalendarToday
} from 'react-icons/md';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, Area, AreaChart, ReferenceLine
} from 'recharts';

const SIGNAL_FIELD_ORDER = ['Speed', 'Temperature', 'Voltage', 'Current', 'Pressure', 'Percentage', 'State', 'Warning', 'Valid'];

const formatSignalName = (name) => name.replace(/_/g, ' ');

const getRawSignals = (frame) => {
  const signals = frame?.signals;
  if (!signals || typeof signals !== 'object') return {};
  return signals.M && typeof signals.M === 'object' ? signals.M : signals;
};

const getSignalNumber = (value) => {
  const rawValue = value && typeof value === 'object'
    ? (value.N ?? value.S)
    : value;
  const numberValue = Number(rawValue);
  return rawValue !== undefined && rawValue !== null && rawValue !== '' && Number.isFinite(numberValue)
    ? numberValue
    : null;
};

const TIME_RANGES = [
  { key: 'latest', label: 'Latest', hours: 0 },
  { key: '15m', label: 'Last 15 min', hours: 0.25 },
  { key: '1h', label: 'Last 1 hour', hours: 1 },
  { key: '4h', label: 'Last 4 hours', hours: 4 },
  { key: '24h', label: 'Last 24 hours', hours: 24 },
  { key: 'custom', label: 'Custom Range', hours: 'custom' },
];

const STATUS_VARIANTS = {
  normal: { label: 'Normal', cls: 'status-badge--normal' },
  warning: { label: 'Warning', cls: 'status-badge--warning' },
  critical: { label: 'Critical', cls: 'status-badge--critical' },
  offline: { label: 'Offline', cls: 'status-badge--offline' },
  unverified: { label: 'Unverified', cls: 'status-badge--offline' },
};

const getUnitForMetric = (metric) => {
  switch (metric) {
    case 'Temperature': return '°C';
    case 'Voltage': return 'V';
    case 'Current': return 'A';
    case 'Pressure': return 'kPa';
    case 'Percentage': return '%';
    case 'Speed': return 'km/h';
    default: return '';
  }
};

function Skeleton({ className = '', count = 1 }) {
  const items = [];
  for (let i = 0; i < count; i++) {
    items.push(<div key={i} className={`ct-skeleton ${className}`} />);
  }
  return <>{items}</>;
}

function StatusBadge({ status, children }) {
  const variant = STATUS_VARIANTS[status] || STATUS_VARIANTS.offline;
  return (
    <span className={`ct-status-badge ${variant.cls}`}>
      <span className="ct-status-badge__dot" />
      {children || variant.label}
    </span>
  );
}

export default function CANTelemetry({ devices = [], user = '' }) {
  const apiKey = useMemo(() => {
    const fromEnv = process.env.REACT_APP_API_KEY;
    if (fromEnv) return fromEnv;
    if (typeof window !== 'undefined' && window.REACT_APP_API_KEY) {
      return window.REACT_APP_API_KEY;
    }
    return '';
  }, []);

  const canApiBase = process.env.REACT_APP_CAN_TELEMETRY_API_BASE || 'https://xgt4hkmvki.execute-api.ap-southeast-1.amazonaws.com';
  const useRelativePath = false;

  // Core state
  const [imeis, setImeis] = useState([]);
  const [selectedImei, setSelectedImei] = useState(null);
  const [imeiDropdownOpen, setImeiDropdownOpen] = useState(false);
  const [imeiSearch, setImeiSearch] = useState('');
  const [loadingImeis, setLoadingImeis] = useState(true);

  const [timeRange, setTimeRange] = useState('1h');
  const [customRangeOpen, setCustomRangeOpen] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [customRangeError, setCustomRangeError] = useState('');

  const [latestFrame, setLatestFrame] = useState(null);
  const [historicalFrames, setHistoricalFrames] = useState([]);
  const [loadingFrames, setLoadingFrames] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [frameError, setFrameError] = useState('');

  const [liveEnabled, setLiveEnabled] = useState(false);
  const [signalSearch, setSignalSearch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('All signals');
  const [expandedFrame, setExpandedFrame] = useState(null);
  const pollIntervalRef = useRef(null);
  const lastRefreshRef = useRef(null);

  // Normalize frame data from API response
  const normalizeFrame = useCallback((rawFrame) => {
    const normalizedSignals = rawFrame?.signals && typeof rawFrame.signals === 'object'
      ? rawFrame.signals
      : {};

    return {
      ...rawFrame,
      signals: normalizedSignals,
      timestamp: rawFrame?.received_at || rawFrame?.timestamp || null,
      can_id: rawFrame?.can_id ?? null,
    };
  }, []);

  // Extract unique IMEIs from devices
  useEffect(() => {
    const uniqueImeis = Array.from(new Set(
      devices
        .filter(d => d.imei)
        .map(d => d.imei)
    )).sort();
    setImeis(uniqueImeis);
    if (uniqueImeis.length > 0 && !selectedImei) {
      setSelectedImei(uniqueImeis[0]);
    }
    setLoadingImeis(false);
  }, [devices, selectedImei]);

  // Fetch latest frame
  const fetchLatestFrame = useCallback(async (imei, options = {}) => {
    if (!imei) return;
    const { setRefreshingState } = options;
    if (setRefreshingState) setRefreshing(true);

    try {
      const url = `${canApiBase}/can-frames/${imei}?latest=true`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(apiKey ? { 'x-api-key': apiKey } : {})
        }
      });

      if (response.ok) {
        const result = await response.json();

        let frame = null;
        if (result?.data && Array.isArray(result.data)) {
          frame = result.data[0];
        } else if (Array.isArray(result)) {
          frame = result[0];
        } else if (result && typeof result === 'object') {
          frame = result;
        }

        if (frame) {
          const normalizedFrame = normalizeFrame(frame);
          setLatestFrame(normalizedFrame);
          setFrameError('');
          lastRefreshRef.current = new Date();
        }
      } else {
        const error = `API Error: ${response.status} ${response.statusText}`;
        setFrameError(error);
      }
    } catch (error) {
      setFrameError(`Connection error: ${error.message}`);
    } finally {
      if (setRefreshingState) setRefreshing(false);
    }
  }, [canApiBase, apiKey, normalizeFrame]);

  // Fetch historical frames
  const fetchHistoricalFrames = useCallback(async (imei, hours) => {
    if (!imei) return;
    setLoadingFrames(true);
    setFrameError('');

    try {
      const candidateUrls = [];

      if (hours !== undefined && hours !== 'custom' && hours !== null && hours !== 0) {
        candidateUrls.push(`${canApiBase}/can-frames/${imei}?hours=${hours}`);
      } else if (hours === 0 || hours === 'latest') {
        candidateUrls.push(`${canApiBase}/can-frames/${imei}?latest=true`);
      }

      if (customStart && customEnd) {
        const startDate = new Date(customStart);
        const endDate = new Date(customEnd);

        if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
          const startIso = encodeURIComponent(startDate.toISOString());
          const endIso = encodeURIComponent(endDate.toISOString());
          candidateUrls.unshift(`${canApiBase}/can-frames/${imei}?start=${startIso}&end=${endIso}`);
        }

        const diffHours = Math.max(1, Math.ceil(Math.abs(new Date(customEnd).getTime() - new Date(customStart).getTime()) / (60 * 60 * 1000)));
        const fallbackHours = Math.min(24, diffHours);
        candidateUrls.push(`${canApiBase}/can-frames/${imei}?hours=${fallbackHours}`);
      }

      let lastError = null;

      for (const url of candidateUrls) {
        try {
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              ...(apiKey ? { 'x-api-key': apiKey } : {})
            }
          });

          if (response.ok) {
            const result = await response.json();

            let frames = [];
            if (result?.data && Array.isArray(result.data)) {
              frames = result.data;
            } else if (Array.isArray(result)) {
              frames = result;
            } else if (result && typeof result === 'object' && hours === 0) {
              frames = [result];
            }

            const normalizedFrames = frames.map(f => normalizeFrame(f));
            setHistoricalFrames(normalizedFrames);
            if (normalizedFrames.length > 0) {
              setLatestFrame(normalizedFrames[normalizedFrames.length - 1]);
              lastRefreshRef.current = new Date();
            }
            return;
          }

          lastError = `API Error: ${response.status} ${response.statusText}`;
        } catch (err) {
          lastError = err.message;
        }
      }

      if (lastError) {
        setFrameError(lastError);
      }
    } catch (error) {
      setFrameError('Error loading frame data. Please try again.');
    } finally {
      setLoadingFrames(false);
    }
  }, [canApiBase, apiKey, customStart, customEnd, normalizeFrame]);

  // Load frames when IMEI or time range changes
  useEffect(() => {
    if (!selectedImei) return;

    const range = TIME_RANGES.find(r => r.key === timeRange);

    if (timeRange === 'custom' && customStart && customEnd) {
      fetchLatestFrame(selectedImei);
      fetchHistoricalFrames(selectedImei, 'custom');
    } else if (timeRange !== 'custom' && range) {
      fetchLatestFrame(selectedImei);
      if (range.hours === 0) {
        fetchHistoricalFrames(selectedImei, 0);
      } else {
        fetchHistoricalFrames(selectedImei, range.hours);
      }
    }
  }, [selectedImei, timeRange, customStart, customEnd, fetchLatestFrame, fetchHistoricalFrames]);

  // Live polling
  useEffect(() => {
    if (!liveEnabled || !selectedImei) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      return;
    }

    pollIntervalRef.current = setInterval(() => {
      fetchLatestFrame(selectedImei);
    }, 30000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [liveEnabled, selectedImei, fetchLatestFrame]);

  // Time formatting
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = typeof timestamp === 'string' ? new Date(timestamp) : new Date(Number(timestamp));
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = typeof timestamp === 'string' ? new Date(timestamp) : new Date(Number(timestamp));
      return date.toLocaleString();
    } catch {
      return '';
    }
  };

  const getTimeSinceUpdate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    try {
      const frameTime = typeof timestamp === 'string' ? new Date(timestamp) : new Date(Number(timestamp));
      const now = new Date();
      const seconds = Math.floor((now - frameTime) / 1000);

      if (seconds < 60) return `${seconds}s ago`;
      if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
      if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
      return `${Math.floor(seconds / 86400)}d ago`;
    } catch {
      return 'Unknown';
    }
  };

  const formatLastUpdated = () => {
    if (!lastRefreshRef.current) return '—';
    return lastRefreshRef.current.toLocaleTimeString();
  };

  // Signal value retrieval
  const getSignalValue = (frame, signalKey) => {
    return getSignalNumber(getRawSignals(frame)[signalKey]);
  };

  const getSpeedValue = useCallback((frame) => {
    if (!frame) return null;
    const rawSignals = getRawSignals(frame);
    const speedKeys = ['Diagnostic_Status_Speed', 'Speed', 'speed', 'Vehicle_Speed'];
    for (const key of speedKeys) {
      const value = getSignalNumber(rawSignals[key]);
      if (value !== null) return value;
    }
    return null;
  }, []);

  // Status indicator
  const getStatusIndicator = (frame) => {
    if (!frame) return { status: 'offline', label: 'Offline', variant: 'offline', Icon: MdOutlineCircle };

    const signals = getRawSignals(frame);
    const warningSignals = Object.entries(signals).filter(([key]) => key.endsWith('_Warning'));
    const validSignals = Object.entries(signals).filter(([key]) => key.endsWith('_Valid'));
    const warning = warningSignals.some(([, value]) => getSignalNumber(value) === 1);
    const valid = validSignals.length > 0 && validSignals.some(([, value]) => getSignalNumber(value) === 1);

    if (validSignals.length > 0 && !valid) {
      return { status: 'critical', label: 'Invalid', variant: 'critical', Icon: MdError };
    }
    if (warning) {
      return { status: 'warning', label: 'Warning', variant: 'warning', Icon: MdWarning };
    }
    return { status: 'normal', label: 'Online', variant: 'normal', Icon: MdCheckCircle };
  };

  const getSubsystemStatus = (groupSignals) => {
    const warning = groupSignals.find(s => s.metric === 'Warning');
    const valid = groupSignals.find(s => s.metric === 'Valid');

    if (valid?.value === 1 && warning?.value !== 1) return 'normal';
    if (warning?.value === 1) return 'warning';
    if (valid?.value === 0 && valid !== undefined) return 'critical';
    return 'unverified';
  };

  // Custom range validation
  const handleApplyCustomRange = () => {
    setCustomRangeError('');

    if (!customStart || !customEnd) {
      setCustomRangeError('Please select both start and end date/time');
      toast.error('Please select both start and end date/time');
      return;
    }

    const startDate = new Date(customStart);
    const endDate = new Date(customEnd);

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      setCustomRangeError('Invalid date or time value');
      toast.error('Invalid date or time value');
      return;
    }

    if (startDate >= endDate) {
      setCustomRangeError('End time must be later than start time');
      toast.error('End time must be later than start time');
      return;
    }

    setTimeRange('custom');
    setCustomRangeOpen(false);
    setCustomRangeError('');
  };

  const handleResetCustomRange = () => {
    setCustomStart('');
    setCustomEnd('');
    setCustomRangeError('');
  };

  // Derived signal data
  const signalEntries = useMemo(() => Object.entries(getRawSignals(latestFrame))
    .map(([key, rawValue]) => {
      const parts = key.split('_');
      const metric = parts.pop() || key;
      return { key, group: parts.join(' '), metric, value: getSignalNumber(rawValue) };
    })
    .sort((a, b) => {
      const groupOrder = a.group.localeCompare(b.group);
      if (groupOrder !== 0) return groupOrder;
      return (SIGNAL_FIELD_ORDER.indexOf(a.metric) - SIGNAL_FIELD_ORDER.indexOf(b.metric)) || a.metric.localeCompare(b.metric);
    }), [latestFrame]);

  const signalGroups = useMemo(() => Array.from(new Set(signalEntries.map(signal => signal.group))), [signalEntries]);

  const visibleSignals = signalEntries.filter(signal => {
    const matchesGroup = selectedGroup === 'All signals' || signal.group === selectedGroup;
    const query = signalSearch.trim().toLowerCase();
    return matchesGroup && (!query || signal.key.toLowerCase().includes(query) || signal.group.toLowerCase().includes(query));
  });

  const warningCount = signalEntries.filter(signal => signal.metric === 'Warning' && signal.value === 1).length;
  const validCount = signalEntries.filter(signal => signal.metric === 'Valid' && signal.value === 1).length;
  const statusIndicator = getStatusIndicator(latestFrame);
  const StatusIcon = statusIndicator.Icon;

  const filteredImeis = useMemo(() => {
    if (!imeiSearch.trim()) return imeis;
    const query = imeiSearch.trim().toLowerCase();
    return imeis.filter(imei => imei.toLowerCase().includes(query));
  }, [imeis, imeiSearch]);

  // Speed chart data
  const speedChartData = useMemo(() => {
    const source = historicalFrames.length > 0 ? historicalFrames : latestFrame ? [latestFrame] : [];
    const sorted = [...source].sort((a, b) => {
      const ta = a?.timestamp || a?.received_at || 0;
      const tb = b?.timestamp || b?.received_at || 0;
      return new Date(ta).getTime() - new Date(tb).getTime();
    });

    const data = sorted.map((frame, index) => {
      const rawTs = frame?.timestamp || frame?.received_at || null;
      const timestampMs = rawTs ? new Date(rawTs).getTime() : null;
      const speed = getSpeedValue(frame);
      return {
        id: index,
        rawTs,
        timeMs: timestampMs,
        timeLabel: rawTs ? formatTime(rawTs) : '',
        fullTime: rawTs ? formatDateTime(rawTs) : '',
        speed: speed !== null ? Number(Number(speed).toFixed(2)) : null,
      };
    }).filter(point => point.speed !== null && point.timeMs !== null);

    // Downsample for large datasets
    if (data.length > 120) {
      const step = Math.ceil(data.length / 120);
      const sampled = data.filter((_, i) => i % step === 0 || i === data.length - 1);
      return sampled;
    }

    return data;
  }, [getSpeedValue, historicalFrames, latestFrame]);

  const speedStats = useMemo(() => {
    if (!speedChartData.length) return { avg: 0, max: 0, min: 0, current: 0 };
    const values = speedChartData.map(p => p.speed).filter(v => v !== null && Number.isFinite(v));
    if (!values.length) return { avg: 0, max: 0, min: 0, current: 0 };
    const total = values.reduce((sum, v) => sum + v, 0);
    return {
      avg: total / values.length,
      max: Math.max(...values),
      min: Math.min(...values),
      current: values[values.length - 1],
    };
  }, [speedChartData]);

  const renderValue = (signal) => signal?.value === null || signal?.value === undefined
    ? '—'
    : Number.isInteger(signal.value) ? signal.value : Number(signal.value).toFixed(2);

  const getGroupSignals = (group) => signalEntries.filter(signal => signal.group === group);

  const handleRefresh = () => {
    if (!selectedImei || refreshing) return;
    fetchLatestFrame(selectedImei, { setRefreshingState: true });
    const range = TIME_RANGES.find(r => r.key === timeRange);
    if (range && range.hours !== 'custom' && range.hours !== 0) {
      fetchHistoricalFrames(selectedImei, range.hours);
    } else if (timeRange === 'custom') {
      fetchHistoricalFrames(selectedImei, 'custom');
    }
  };

  const handleRetry = () => {
    if (!selectedImei) return;
    const range = TIME_RANGES.find(r => r.key === timeRange);
    fetchLatestFrame(selectedImei);
    if (timeRange === 'custom') {
      fetchHistoricalFrames(selectedImei, 'custom');
    } else if (range) {
      fetchHistoricalFrames(selectedImei, range.hours === 0 ? 0 : range.hours);
    }
  };

  const formatTimeMs = (ms) => {
    if (!ms) return '';
    try {
      return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="ct-chart-tooltip">
          <div className="ct-chart-tooltip__title">Vehicle Speed</div>
          <div className="ct-chart-tooltip__time">{data.fullTime}</div>
          <div className="ct-chart-tooltip__value">
            <span className="ct-chart-tooltip__number">{data.speed}</span>
            <span className="ct-chart-tooltip__unit">km/h</span>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="ct-wrapper">
      {/* ========== HEADER ========== */}
      <header className="ct-header ct-header--compact">
        <div className="ct-header__top">
          <div className="ct-header__title-block">
            <h1 className="ct-header__title">CAN Telemetry Dashboard</h1>
            <p className="ct-header__subtitle">Real-time EV vehicle diagnostic signals & CAN bus monitoring</p>
          </div>

          <div className="ct-header__statusline">
            <div className="ct-meta-row">
              <span className="ct-meta-row__label">Connection:</span>
              <StatusBadge status={statusIndicator.variant}>
                <StatusIcon /> {statusIndicator.label}
              </StatusBadge>
            </div>
            <div className="ct-meta-row">
              <span className="ct-meta-row__label">Last updated:</span>
              <span className="ct-meta-row__value">{formatLastUpdated()}</span>
            </div>
            <div className="ct-header__buttons">
              <button
                className={`ct-btn ct-btn--toggle ${liveEnabled ? 'is-on' : ''}`}
                onClick={() => setLiveEnabled(!liveEnabled)}
                title="Auto-refresh every 30 seconds"
              >
                <span className={`ct-live-dot ${liveEnabled ? 'is-pulsing' : ''}`} />
                Auto {liveEnabled ? 'ON' : 'OFF'}
              </button>
              <button
                className="ct-btn ct-btn--primary"
                onClick={handleRefresh}
                disabled={refreshing || !selectedImei}
              >
                <MdRefresh className={`ct-icon ${refreshing ? 'is-spinning' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Device Selector with horizontal dropdown */}
        <div className="ct-device-selector">
          <label className="ct-device-selector__label">
            <MdSearch /> Device / IMEI
          </label>
          <div className="ct-dropdown ct-dropdown--horizontal">
            <button
              className={`ct-dropdown__trigger ${imeiDropdownOpen ? 'is-open' : ''}`}
              onClick={() => setImeiDropdownOpen(!imeiDropdownOpen)}
              disabled={loadingImeis}
            >
              <span className="ct-dropdown__value">
                {loadingImeis ? 'Loading devices...' : (selectedImei || 'Select IMEI')}
              </span>
              {selectedImei && !loadingImeis && (
                <StatusBadge status={statusIndicator.variant}>
                  {statusIndicator.label}
                </StatusBadge>
              )}
              <MdExpandMore className="ct-dropdown__icon" />
            </button>

            {imeiDropdownOpen && (
              <div className="ct-dropdown__panel ct-dropdown__panel--horizontal">
                <div className="ct-dropdown__search">
                  <MdSearch />
                  <input
                    type="text"
                    placeholder="Search IMEI..."
                    value={imeiSearch}
                    onChange={(e) => setImeiSearch(e.target.value)}
                    autoFocus
                  />
                  {imeiSearch && (
                    <button
                      className="ct-search-field__clear"
                      onClick={() => setImeiSearch('')}
                      aria-label="Clear search"
                    >
                      <MdClose />
                    </button>
                  )}
                </div>
                <div className="ct-dropdown__list ct-dropdown__list--horizontal">
                  {loadingImeis ? (
                    <div className="ct-dropdown__empty">Loading devices...</div>
                  ) : filteredImeis.length === 0 ? (
                    <div className="ct-dropdown__empty">
                      {imeiSearch ? 'No matching devices' : 'No devices available'}
                    </div>
                  ) : (
                    filteredImeis.map(imei => (
                      <button
                        key={imei}
                        className={`ct-dropdown__item ct-dropdown__item--chip ${selectedImei === imei ? 'is-active' : ''}`}
                        onClick={() => {
                          setSelectedImei(imei);
                          setImeiDropdownOpen(false);
                          setImeiSearch('');
                        }}
                        title={imei}
                      >
                        <span className="ct-status-dot ct-status-dot--online" />
                        <span className="ct-dropdown__item-imei">{imei}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ========== FILTER TOOLBAR ========== */}
      <section className="ct-filter-bar">
        <div className="ct-filter-bar__left">
          <MdCalendarToday className="ct-filter-bar__icon" />
          <span className="ct-filter-bar__label">Time Range</span>
          <div className="ct-time-buttons">
            {TIME_RANGES.map((range) => (
              <button
                key={range.key}
                className={`ct-time-btn ${timeRange === range.key ? 'is-active' : ''}`}
                onClick={() => {
                  if (range.key === 'custom') {
                    setCustomRangeOpen(!customRangeOpen);
                  } else {
                    setCustomRangeOpen(false);
                    setTimeRange(range.key);
                  }
                }}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>

        <div className="ct-filter-bar__right">
          {latestFrame?.timestamp && (
            <div className="ct-frame-info">
              <span className="ct-frame-info__label">Frame Age:</span>
              <span className="ct-frame-info__value">{getTimeSinceUpdate(latestFrame.timestamp)}</span>
            </div>
          )}
        </div>
      </section>

      {/* Custom range picker */}
      {customRangeOpen && (
        <div className="ct-custom-range">
          <div className="ct-custom-range__grid">
            <div className="ct-input-group">
              <label>Start Date & Time</label>
              <input
                type="datetime-local"
                value={customStart}
                onChange={(e) => {
                  setCustomStart(e.target.value);
                  setCustomRangeError('');
                }}
              />
            </div>
            <div className="ct-input-group">
              <label>End Date & Time</label>
              <input
                type="datetime-local"
                value={customEnd}
                onChange={(e) => {
                  setCustomEnd(e.target.value);
                  setCustomRangeError('');
                }}
              />
            </div>
            <div className="ct-custom-range__buttons">
              <button
                className="ct-btn ct-btn--apply"
                onClick={handleApplyCustomRange}
              >
                Apply Range
              </button>
              <button
                className="ct-btn ct-btn--ghost"
                onClick={handleResetCustomRange}
              >
                Reset
              </button>
            </div>
          </div>
          {customRangeError && (
            <div className="ct-custom-range__error">
              <MdWarning /> {customRangeError}
            </div>
          )}
        </div>
      )}

      {/* No device selected state */}
      {!selectedImei && !loadingImeis && (
        <div className="ct-empty-state">
          <MdTune className="ct-empty-state__icon" />
          <h3 className="ct-empty-state__title">Select a device to inspect CAN telemetry</h3>
          <p className="ct-empty-state__desc">Choose a device IMEI from the selector above to load real-time diagnostic signals.</p>
        </div>
      )}

      {loadingImeis && (
        <div className="ct-empty-state">
          <MdAutorenew className="ct-empty-state__icon is-spinning" />
          <h3 className="ct-empty-state__title">Loading devices...</h3>
        </div>
      )}

      {/* ========== MAIN CONTENT ========== */}
      {selectedImei && !loadingImeis && (
        <>
          {/* FRAME OVERVIEW */}
          <section className="ct-overview">
            <div className="ct-overview__card ct-overview__card--status">
              <div className={`ct-overview__icon ct-overview__icon--${statusIndicator.variant}`}>
                <StatusIcon />
              </div>
              <div className="ct-overview__content">
                <span className="ct-overview__label">Frame Status</span>
                <strong className="ct-overview__value">{statusIndicator.label}</strong>
              </div>
            </div>

            <div className="ct-overview__card">
              <span className="ct-overview__label">Signals Received</span>
              {loadingFrames ? (
                <Skeleton className="ct-skeleton--text-lg" />
              ) : (
                <strong className="ct-overview__value">
                  {latestFrame?.signal_count?.N || signalEntries.length || 0}
                  <small className="ct-overview__small"> / 100</small>
                </strong>
              )}
            </div>

            <div className="ct-overview__card">
              <span className="ct-overview__label">Valid Signals</span>
              {loadingFrames ? (
                <Skeleton className="ct-skeleton--text-lg" />
              ) : (
                <strong className="ct-overview__value ct-text--success">{validCount}</strong>
              )}
            </div>

            <div className="ct-overview__card">
              <span className="ct-overview__label">Active Warnings</span>
              {loadingFrames ? (
                <Skeleton className="ct-skeleton--text-lg" />
              ) : (
                <strong className={`ct-overview__value ${warningCount > 0 ? 'ct-text--warning' : 'ct-text--success'}`}>
                  {warningCount}
                </strong>
              )}
            </div>

            <div className="ct-overview__card ct-overview__card--meta">
              <div className="ct-overview__meta-row">
                <span className="ct-overview__meta-label">CAN ID</span>
                <span className="ct-overview__meta-value">
                  {latestFrame?.can_id?.N || latestFrame?.can_id || '—'}
                </span>
              </div>
              <div className="ct-overview__meta-row">
                <span className="ct-overview__meta-label">Timestamp</span>
                <span className="ct-overview__meta-value">
                  {formatDateTime(latestFrame?.timestamp) || '—'}
                </span>
              </div>
            </div>
          </section>

          {/* Error banner */}
          {frameError && (
            <div className="ct-error-banner">
              <MdError className="ct-error-banner__icon" />
              <div className="ct-error-banner__content">
                <strong>Unable to load CAN telemetry.</strong>
                <span>{frameError}</span>
              </div>
              <button className="ct-btn ct-btn--ghost ct-error-banner__retry" onClick={handleRetry}>
                <MdRefresh /> Retry
              </button>
            </div>
          )}

          {/* ========== SPEED TELEMETRY CHART ========== */}
          <section className="ct-chart-section">
            <div className="ct-chart-section__header">
              <div className="ct-chart-section__title-block">
                <h2 className="ct-section-title">CAN Telemetry</h2>
                <p className="ct-section-subtitle">
                  Vehicle Speed • {selectedImei} •{' '}
                  {TIME_RANGES.find(r => r.key === timeRange)?.label || 'Custom Range'}
                </p>
              </div>
              <div className="ct-chart-section__toolbar ct-chart-section__toolbar--compact">
                <div className="ct-toolbar-chip">
                  Signal: <strong>Speed</strong>
                </div>
              </div>
            </div>

            {/* Speed summary stats */}
            <div className="ct-speed-stats">
              <div className="ct-speed-stat">
                <span className="ct-speed-stat__label">Current</span>
                {loadingFrames ? (
                  <Skeleton className="ct-skeleton--text-xl" />
                ) : (
                  <strong className="ct-speed-stat__value">
                    {Number(speedStats.current || 0).toFixed(1)}
                    <small>km/h</small>
                  </strong>
                )}
              </div>
              <div className="ct-speed-stat">
                <span className="ct-speed-stat__label">Average</span>
                {loadingFrames ? (
                  <Skeleton className="ct-skeleton--text-xl" />
                ) : (
                  <strong className="ct-speed-stat__value">
                    {Number(speedStats.avg || 0).toFixed(1)}
                    <small>km/h</small>
                  </strong>
                )}
              </div>
              <div className="ct-speed-stat">
                <span className="ct-speed-stat__label">Maximum</span>
                {loadingFrames ? (
                  <Skeleton className="ct-skeleton--text-xl" />
                ) : (
                  <strong className="ct-speed-stat__value ct-text--primary">
                    {Number(speedStats.max || 0).toFixed(1)}
                    <small>km/h</small>
                  </strong>
                )}
              </div>
              <div className="ct-speed-stat">
                <span className="ct-speed-stat__label">Minimum</span>
                {loadingFrames ? (
                  <Skeleton className="ct-skeleton--text-xl" />
                ) : (
                  <strong className="ct-speed-stat__value">
                    {Number(speedStats.min || 0).toFixed(1)}
                    <small>km/h</small>
                  </strong>
                )}
              </div>
              <div className="ct-speed-stat ct-speed-stat--points">
                <span className="ct-speed-stat__label">Data Points</span>
                {loadingFrames ? (
                  <Skeleton className="ct-skeleton--text-xl" />
                ) : (
                  <strong className="ct-speed-stat__value">
                    {speedChartData.length}
                    <small>frames</small>
                  </strong>
                )}
              </div>
            </div>

            {/* Chart */}
            <div className="ct-chart-card">
              {loadingFrames ? (
                <div className="ct-chart-loading">
                  <div className="ct-chart-skeleton">
                    <Skeleton className="ct-skeleton--chart" />
                  </div>
                </div>
              ) : frameError ? (
                <div className="ct-chart-empty">
                  <MdError className="ct-chart-empty__icon ct-text--critical" />
                  <h4>Telemetry data unavailable</h4>
                  <p>{frameError}</p>
                  <button className="ct-btn ct-btn--primary" onClick={handleRetry}>
                    <MdRefresh /> Retry
                  </button>
                </div>
              ) : speedChartData.length === 0 ? (
                <div className="ct-chart-empty">
                  <MdTune className="ct-chart-empty__icon" />
                  <h4>No telemetry data available</h4>
                  <p>No CAN frames found for the selected filters.</p>
                  <button className="ct-btn ct-btn--primary" onClick={handleRefresh}>
                    <MdRefresh /> Refresh Data
                  </button>
                </div>
              ) : (
                <div className="ct-chart-container">
                  <ResponsiveContainer width="100%" height={520}>
                    <AreaChart
                      data={speedChartData}
                      margin={{ top: 24, right: 56, left: 56, bottom: 72 }}
                    >
                      <defs>
                        <linearGradient id="speedGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0f766e" stopOpacity={0.28} />
                          <stop offset="100%" stopColor="#0f766e" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis
                        dataKey="timeMs"
                        type="number"
                        domain={['dataMin', 'dataMax']}
                        stroke="#94a3b8"
                        tick={{ fontSize: 12, fill: '#475569', fontWeight: 500 }}
                        tickLine={{ stroke: '#cbd5e1', strokeWidth: 1 }}
                        axisLine={{ stroke: '#cbd5e1', strokeWidth: 1 }}
                        ticks={speedChartData.length > 0 ? (() => {
                          const min = speedChartData[0].timeMs;
                          const max = speedChartData[speedChartData.length - 1].timeMs;
                          const total = max - min;
                          const slots = 5;
                          const step = Math.max(total / (slots + 1), 60000);
                          const res = [];
                          for (let i = 1; i <= slots; i++) {
                            res.push(Math.round(min + step * i));
                          }
                          const last = res[res.length - 1];
                          if (max - last < step * 0.55) {
                            res.pop();
                          }
                          return res;
                        })() : undefined}
                        interval={0}
                        minTickGap={88}
                        height={60}
                        tickFormatter={(ms) => formatTimeMs(ms)}
                        label={{
                          value: 'Time (HH:MM)',
                          position: 'insideBottom',
                          offset: 0,
                          style: {
                            fill: '#475569',
                            fontSize: 12,
                            fontWeight: 600,
                            textAnchor: 'middle',
                          },
                        }}
                      />
                      <YAxis
                        stroke="#94a3b8"
                        tick={{ fontSize: 12, fill: '#64748b' }}
                        tickLine={{ stroke: '#cbd5e1' }}
                        axisLine={{ stroke: '#cbd5e1' }}
                        domain={[0, 'auto']}
                        allowDecimals={false}
                        tickFormatter={(v) => `${v}`}
                        label={{
                          value: 'Speed (km/h)',
                          angle: -90,
                          position: 'insideLeft',
                          style: {
                            fill: '#64748b',
                            fontSize: 12,
                            fontWeight: 600,
                            textAnchor: 'middle',
                          },
                          offset: 10,
                        }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <ReferenceLine
                        y={0}
                        stroke="#94a3b8"
                        strokeDasharray="3 3"
                        strokeWidth={1.2}
                      />
                      <Legend
                        verticalAlign="top"
                        height={36}
                        iconType="circle"
                        wrapperStyle={{
                          fontSize: '13px',
                          color: '#475569',
                          fontWeight: 500,
                          paddingBottom: '12px',
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="speed"
                        name="Vehicle Speed"
                        stroke="#0f766e"
                        strokeWidth={2.5}
                        fill="url(#speedGradient)"
                        dot={false}
                        activeDot={{
                          r: 6,
                          stroke: '#fff',
                          strokeWidth: 2,
                          fill: '#0f766e',
                        }}
                        connectNulls
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </section>

          {/* ========== SUBSYSTEM SNAPSHOT ========== */}
          <section className="ct-subsystems">
            <div className="ct-section-header">
              <div>
                <span className="ct-eyebrow">Live Frame</span>
                <h2 className="ct-section-title">Subsystem Snapshot</h2>
              </div>
              <span className="ct-count-badge">{signalGroups.length} Subsystems</span>
            </div>

            {loadingFrames ? (
              <div className="ct-subsystem-grid">
                {[0, 1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="ct-sub-card ct-skeleton-card">
                    <div className="ct-sub-card__header">
                      <div className="ct-skeleton ct-skeleton--text-sm" style={{ width: '40%' }} />
                      <div className="ct-skeleton ct-skeleton--badge" />
                    </div>
                    <div className="ct-sub-card__metrics">
                      <div className="ct-metric">
                        <div className="ct-skeleton ct-skeleton--text-sm" style={{ width: '60%' }} />
                        <div className="ct-skeleton ct-skeleton--text-lg" style={{ width: '80%' }} />
                      </div>
                      <div className="ct-metric">
                        <div className="ct-skeleton ct-skeleton--text-sm" style={{ width: '60%' }} />
                        <div className="ct-skeleton ct-skeleton--text-lg" style={{ width: '80%' }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : signalGroups.length === 0 ? (
              <div className="ct-empty-state ct-empty-state--inline">
                <MdTune className="ct-empty-state__icon" />
                <h3 className="ct-empty-state__title">No subsystem data available</h3>
                <p className="ct-empty-state__desc">No CAN signals received for the selected time range.</p>
              </div>
            ) : (
              <div className="ct-subsystem-grid">
                {signalGroups.map(group => {
                  const groupSignals = getGroupSignals(group);
                  const subsystemStatus = getSubsystemStatus(groupSignals);
                  const metricSignals = groupSignals.filter(s => !['State', 'Warning', 'Valid'].includes(s.metric));
                  const stateValue = groupSignals.find(s => s.metric === 'State');
                  const hasWarning = groupSignals.find(s => s.metric === 'Warning')?.value === 1;
                  const isValid = groupSignals.find(s => s.metric === 'Valid')?.value === 1;

                  return (
                    <article
                      key={group}
                      className={`ct-sub-card ${subsystemStatus === 'warning' ? 'ct-sub-card--warning' : ''} ${subsystemStatus === 'critical' ? 'ct-sub-card--critical' : ''}`}
                    >
                      <div className="ct-sub-card__header">
                        <div className="ct-sub-card__title-wrap">
                          <span className="ct-sub-card__index">
                            {String(signalGroups.indexOf(group) + 1).padStart(2, '0')}
                          </span>
                          <h3 className="ct-sub-card__title">{group}</h3>
                        </div>
                        <StatusBadge status={subsystemStatus === 'normal' ? 'normal' : subsystemStatus === 'warning' ? 'warning' : subsystemStatus === 'critical' ? 'critical' : 'unverified'} />
                      </div>

                      {metricSignals.length > 0 ? (
                        <div className={`ct-sub-card__metrics ${metricSignals.length === 1 ? 'is-single' : ''}`}>
                          {metricSignals.map(signal => (
                            <div className="ct-metric" key={signal.key}>
                              <span className="ct-metric__label">{signal.metric}</span>
                              <div className="ct-metric__value-wrap">
                                <strong className="ct-metric__value">{renderValue(signal)}</strong>
                                <small className="ct-metric__unit">{getUnitForMetric(signal.metric)}</small>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="ct-sub-card__no-metrics">
                          <span className="ct-text-muted">No numeric metrics</span>
                        </div>
                      )}

                      <div className="ct-sub-card__footer">
                        <div className="ct-sub-card__footer-item">
                          <span className="ct-sub-card__footer-label">State</span>
                          <span className="ct-sub-card__footer-value">{stateValue?.value ?? '—'}</span>
                        </div>
                        <div className="ct-sub-card__footer-item ct-sub-card__footer-item--right">
                          {hasWarning ? (
                            <span className="ct-text--warning">
                              <MdWarning /> Warning
                            </span>
                          ) : isValid ? (
                            <span className="ct-text--success">
                              <MdCheckCircle /> OK
                            </span>
                          ) : (
                            <span className="ct-text-muted">
                              <MdOutlineCircle /> Unverified
                            </span>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          {/* ========== SIGNAL DIRECTORY TABLE ========== */}
          <section className="ct-signal-directory">
            <div className="ct-section-header">
              <div>
                <span className="ct-eyebrow">Complete Payload</span>
                <h2 className="ct-section-title">Signal Directory</h2>
              </div>
              <span className="ct-count-badge">
                {visibleSignals.length} of {signalEntries.length} Signals
              </span>
            </div>

            <div className="ct-directory-toolbar">
              <div className="ct-search-field">
                <MdSearch />
                <input
                  type="text"
                  placeholder="Search signal names or subsystems..."
                  value={signalSearch}
                  onChange={(e) => setSignalSearch(e.target.value)}
                />
                {signalSearch && (
                  <button
                    className="ct-search-field__clear"
                    onClick={() => setSignalSearch('')}
                    aria-label="Clear search"
                  >
                    <MdClose />
                  </button>
                )}
              </div>
              <div className="ct-select-field">
                <select
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                >
                  <option value="All signals">All Subsystems</option>
                  {signalGroups.map(group => (
                    <option key={group} value={group}>{group}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="ct-table-wrap">
              {loadingFrames ? (
                <div className="ct-table-skeleton">
                  <Skeleton count={8} className="ct-skeleton--table-row" />
                </div>
              ) : visibleSignals.length === 0 ? (
                <div className="ct-empty-state ct-empty-state--inline">
                  <MdSearch className="ct-empty-state__icon" />
                  <h3 className="ct-empty-state__title">No signals match this filter</h3>
                  <p className="ct-empty-state__desc">Try adjusting the search query or subsystem filter.</p>
                </div>
              ) : (
                <table className="ct-table">
                  <thead>
                    <tr>
                      <th className="ct-table__th">Signal</th>
                      <th className="ct-table__th">Subsystem</th>
                      <th className="ct-table__th ct-table__th--num">Value</th>
                      <th className="ct-table__th">Type</th>
                      <th className="ct-table__th">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleSignals.map(signal => (
                      <tr key={signal.key} className="ct-table__tr">
                        <td className="ct-table__td ct-table__td--mono">
                          {signal.key}
                        </td>
                        <td className="ct-table__td">
                          <span className="ct-group-pill">{signal.group}</span>
                        </td>
                        <td className="ct-table__td ct-table__td--num ct-table__td--mono">
                          {renderValue(signal)}
                          {getUnitForMetric(signal.metric) && (
                            <span className="ct-table__unit"> {getUnitForMetric(signal.metric)}</span>
                          )}
                        </td>
                        <td className="ct-table__td">{signal.metric}</td>
                        <td className="ct-table__td">
                          {signal.metric === 'Warning' ? (
                            signal.value === 1
                              ? <StatusBadge status="warning">Warning</StatusBadge>
                              : <StatusBadge status="normal">Clear</StatusBadge>
                          ) : signal.metric === 'Valid' ? (
                            signal.value === 1
                              ? <StatusBadge status="normal">Valid</StatusBadge>
                              : <StatusBadge status="offline">Unverified</StatusBadge>
                          ) : (
                            <StatusBadge status="normal">Received</StatusBadge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          {/* ========== FRAME HISTORY ========== */}
          <section className="ct-history">
            <div className="ct-section-header">
              <div>
                <span className="ct-eyebrow">Recent Frames</span>
                <h2 className="ct-section-title">Frame History</h2>
              </div>
              <span className="ct-count-badge">{historicalFrames.length} Frames</span>
            </div>

            <div className="ct-history__content">
              {loadingFrames ? (
                <div className="ct-table-skeleton">
                  <Skeleton count={5} className="ct-skeleton--table-row" />
                </div>
              ) : historicalFrames.length === 0 ? (
                <div className="ct-empty-state ct-empty-state--inline">
                  <MdTune className="ct-empty-state__icon" />
                  <h3 className="ct-empty-state__title">No frame history available</h3>
                  <p className="ct-empty-state__desc">No CAN frames were captured for this time window.</p>
                </div>
              ) : (
                <div className="ct-history-list">
                  {historicalFrames.slice(-8).reverse().map((frame, index) => {
                    const frameSignals = Object.keys(getRawSignals(frame)).length;
                    const isExpanded = expandedFrame === index;
                    return (
                      <div key={`${frame.timestamp}-${index}`} className="ct-history-row">
                        <div className="ct-history-row__main">
                          <span className="ct-history-row__time">
                            {formatDateTime(frame.timestamp)}
                          </span>
                          <span className="ct-history-row__canid">
                            CAN {frame.can_id?.N || frame.can_id || '—'}
                          </span>
                          <span className="ct-history-row__signals">
                            {frameSignals} signals
                          </span>
                          <StatusBadge status="normal">Captured</StatusBadge>
                          <button
                            className="ct-btn ct-btn--ghost ct-btn--sm"
                            onClick={() => setExpandedFrame(isExpanded ? null : index)}
                          >
                            {isExpanded ? 'Hide JSON' : 'View JSON'}
                          </button>
                        </div>
                        {isExpanded && (
                          <pre className="ct-history-row__json">
                            {JSON.stringify(frame, null, 2)}
                          </pre>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
